import type { Attachment } from '@/graphql/generated';
import type { Ref } from 'vue';
import { getConfigValue } from '@/core/consts/config';
import { FileType, type LoadedFile, type ProcessFileRules } from '@/core/types/files';
import {
  getFileExtension,
  getImageLocalUrl,
  imageExtensions,
  isIamgeFile,
  isImageExtension,
  isPdfExtension,
  loadImage,
  pdfExtensions,
} from '@/core/utils/file';
import { useContentStore } from '@/parts/content/store';
import { ContentSection } from '@/parts/content/types';

export const acceptFiles = `${imageExtensions}, ${pdfExtensions}`;

export const filesRules: ProcessFileRules = {
  maxSize: getConfigValue('contentServiceMaxUploadFileSize'),
  extension: acceptFiles,
  custom: async (file) => {
    const isImage = isIamgeFile(file);

    if (!isImage) {
      return undefined;
    }

    const src = getImageLocalUrl(file);

    const image = await loadImage(src);

    if (image.width < image.height) {
      return 'Изображение должно быть горизонтальным';
    }

    return undefined;
  },
};

export const useContentImagesUpload = (accommodationObjectCid: Ref<string | undefined>) => {
  const { uploadContentImage, getContentServiceImage, deleteContentImage } = useContentStore();

  const uploadImages = async (
    images: LoadedFile[],
    attachments: Attachment[],
    section: ContentSection,
    sectionObjectName: string | null | undefined = null,
    questionnaireId: string | null | undefined = null,
  ) => {
    await Promise.all(
      images
        .filter((image) => !image.error && image.file && !image.uploaded)
        .map((image) =>
          (async () => {
            if (!accommodationObjectCid.value) {
              return;
            }
            try {
              image.loading = true;

              const response = await uploadContentImage({
                accommodationObjectCid: accommodationObjectCid.value,
                section,
                sectionObjectName,
                attachment: image.file!,
              });

              if (!response?.data) {
                image.error = 'Не удалось загрузить фотографию';
              } else {
                image.id = response.data.UploadContentImage.fileId;

                attachments.push({
                  id: response.data.UploadContentImage.fileId,
                  name: response.data.UploadContentImage.fileName,
                  size: response.data.UploadContentImage.fileSize,
                  questionnaireId,
                });
              }

              image.uploaded = true;
            } catch (error) {
              console.error(error);

              throw error;
            }
          })(),
        ),
    );

    images.forEach((image) => {
      image.loading = false;
    });
  };

  const initImages = async (
    attachments: Attachment[],
    imgCollection: LoadedFile[],
    section: ContentSection,
    sectionObjectName: string | null | undefined = null,
  ) => {
    attachments.forEach(async (attachment, index) => {
      if (!accommodationObjectCid.value) {
        return;
      }

      const extension = `.${getFileExtension(attachment.name)}`.toLowerCase();
      const type = isImageExtension(extension)
        ? FileType.IMAGE
        : isPdfExtension(extension)
          ? FileType.PDF
          : FileType.OTHER;

      const img: LoadedFile = {
        id: attachment.id,
        file: null,
        name: attachment.name,
        extension,
        type,
        size: attachment.size,
        url: undefined,
        error: '',
        loading: true,
        uploaded: false,
      };

      imgCollection.push(img);
      try {
        const response = await getContentServiceImage({
          fileId: attachment.id,
          section,
          sectionObjectName,
          accommodationObjectCid: accommodationObjectCid.value,
        });

        const file = response.data
          ? new File([response.data.GetContentServicImage.data], attachment.name, {
              type: type === FileType.PDF ? 'application/pdf' : 'image/jpg',
            })
          : null;

        imgCollection[index].file = file;
        imgCollection[index].url = file ? getImageLocalUrl(file) : undefined;
        imgCollection[index].loading = false;
        imgCollection[index].uploaded = true;
      } catch (e) {
        imgCollection[index].loading = false;
        imgCollection[index].error = 'Не удалось загрузить фотографию';
      }
    });
  };

  const deleteImage = async (
    fileId: string,
    section: ContentSection,
    sectionObjectName: string | null | undefined = null,
    attachments: Attachment[],
  ) => {
    if (!accommodationObjectCid.value) {
      return false;
    }

    const attachment = attachments.find((attachment) => attachment.id === fileId);

    let result = true;

    if (attachment) {
      const response = await deleteContentImage({
        fileId,
        section,
        sectionObjectName,
        accommodationObjectCid: accommodationObjectCid.value,
      });

      result = !!response.data;

      if (result) {
        attachments.splice(
          attachments.findIndex((attachment) => attachment.id === fileId),
          1,
        );
      }
    }

    return result;
  };

  return {
    uploadImages,
    initImages,
    deleteImage,
  };
};
